import { useEffect, useState } from "react";
import { Eye, Loader2, CloudOff, RotateCw } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Switch } from "./ui/switch";
import LoadFailureNote from "./LoadFailureNote";
import { ToggleSwitch } from "./kit/ToggleSwitch";
import { FailureCard } from "./GoalNotice";
import api from "../lib/api";
import { toast } from "sonner";
import {
  NOTHING_SHARED,
  READ_ONLY_NOTICE,
  SHAREABLE_SECTIONS,
  SHARING_HEADING,
  SHARING_LIMIT,
  describeVisibility,
  readOneToOneAudience,
  toVisibilityPayload,
} from "../lib/identitySharing";

/**
 * The player's control over who reads their Identity & Vision (issue #292
 * slice 1), at the top of the Identity & Vision tab.
 *
 * ============================================================
 * THE STATE IS A SENTENCE, AND IT IS ON THE PAGE
 * ============================================================
 * #292 work item 3 is explicit: *"a control on the Identity & Vision tab
 * that states in plain words who can currently see what — the state must be
 * readable without opening a dialog"*. So this is a card, not a dialog, not
 * a settings link, and not a row of switches whose meaning you assemble
 * yourself. `describeVisibility` produces one sentence naming the coach and
 * naming the sections, and it renders above the switches, in normal-weight
 * body text, before any interaction.
 *
 * The switches are underneath because they are the ACTION and the sentence
 * is the STATE. A player scanning this page should be able to answer "who
 * can see my writing" without touching anything — three toggle positions
 * are not an answer to that question, they are three facts you have to
 * combine with a fourth (is a coach even linked) that the switches do not
 * show.
 *
 * ============================================================
 * IT IS AT THE TOP OF THE TAB, NOT THE BOTTOM
 * ============================================================
 * The decision about who reads this comes BEFORE the writing, the way
 * #287's sharing notice sits above the health fields rather than under the
 * Save button. A player who scrolls past three long sections and finds the
 * sharing control at the end has already decided what to write under an
 * assumption nobody told them was wrong.
 *
 * ============================================================
 * NOT RENDERED IN THE ONBOARDING WALK
 * ============================================================
 * `PlayerIdentity.jsx` renders `embedded` as step 2 of the first-run
 * walkthrough (#289), which asks for Player Vision and nothing else. A
 * sharing control there would be a second decision inside a step designed
 * to ask for one thing, at the moment a brand-new account has no linked
 * coach at all — so the honest answer to "who can see this" is "nobody",
 * and the control has nothing to do. Default-off means skipping it discloses
 * nothing; the control is waiting on the tab when they arrive with something
 * to share.
 *
 * ============================================================
 * A FAILED LOAD IS A FAILURE, NOT "NOTHING IS SHARED"
 * ============================================================
 * #271, and on this surface the swallowed-failure lie is specific: a card
 * that renders three off switches after a failed fetch tells the player
 * their writing is private when the server may say otherwise. So a load
 * failure renders `LoadFailureNote` and NO switches — there is nothing
 * truthful to draw.
 *
 * A failed SAVE is the same rule one step later: the switch snaps back to
 * where it was and says so. An optimistic toggle that stayed on after a
 * rejected write would be the app telling the player they had shared
 * something they had not — or, worse, that they had stopped sharing
 * something they had not stopped sharing.
 */
export default function IdentitySharingCard({ appearance = "shipped" }) {
  const r5 = appearance === "r5";
  const [sections, setSections] = useState(NOTHING_SHARED);
  const [readers, setReaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(null);
  // #1290: on the new look a refused switch is a line on the card (D48, D57),
  // not a toast.
  const [refused, setRefused] = useState(false);

  const load = () => {
    setLoading(true);
    setFailed(false);
    return api
      .get("/players/me/identity/visibility")
      .then((r) => {
        const state = readOneToOneAudience(r.data);
        setSections(state.sections);
        setReaders(state.readers);
        setLoading(false);
      })
      .catch(() => {
        setFailed(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (key, next) => {
    const previous = sections;
    const wanted = { ...sections, [key]: next };
    // Optimistic, then reconciled against what the server actually stored —
    // never left as a guess. The API answers with the whole audience state,
    // so the switches end up showing the stored truth rather than the
    // request.
    setSections(wanted);
    setRefused(false);
    setSaving(key);
    try {
      const r = await api.put("/players/me/identity/visibility", toVisibilityPayload(wanted));
      const state = readOneToOneAudience(r.data);
      setSections(state.sections);
      setReaders(state.readers);
    } catch (e) {
      setSections(previous);
      if (r5) setRefused(true);
      else toast.error("Couldn't change what you're sharing — nothing has changed.");
    } finally {
      setSaving(null);
    }
  };

  if (r5) {
    return (
      <div
        data-testid="identity-sharing-card"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: 14,
          minWidth: 0,
          background: "var(--nw-surface)",
          border: "1px solid var(--nw-line)",
          borderRadius: "var(--nw-radius-card)",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: 10,
              background: "var(--nw-navy-tint)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Eye size={18} color="var(--nw-navy)" aria-hidden="true" />
          </div>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 16, color: "var(--nw-ink)" }}>{SHARING_HEADING}</h3>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: "var(--nw-ink-muted)" }}>
              Identity &amp; Vision is private by default. Nothing below is shared until you switch it on.
            </p>
          </div>
        </div>

        {failed && (
          <div data-testid="identity-sharing-load-failed" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CloudOff size={20} color="var(--nw-alert)" strokeWidth={2} aria-hidden="true" style={{ flexShrink: 0 }} />
              <div style={{ fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }}>Couldn't load who can see your Identity &amp; Vision</div>
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>Nothing has been lost — we just couldn't reach the server.</div>
            <button
              type="button"
              onClick={load}
              data-testid="identity-sharing-load-failed-retry"
              style={{
                alignSelf: "flex-start",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                minHeight: 44,
                padding: "0 14px",
                background: "var(--nw-surface)",
                border: "1px solid var(--nw-line-strong)",
                borderRadius: "var(--nw-radius-control)",
                fontSize: 14,
                fontWeight: 600,
                color: "var(--nw-navy)",
                cursor: "pointer",
              }}
            >
              <RotateCw size={14} strokeWidth={2} aria-hidden="true" />
              Try again
            </button>
          </div>
        )}

        {!failed && loading && (
          <div
            data-testid="identity-sharing-loading"
            aria-busy="true"
            style={{ height: 120, display: "flex", flexDirection: "column", gap: 10, padding: 14, boxSizing: "border-box", background: "var(--nw-navy-tint)", borderRadius: "var(--nw-radius-control)" }}
          >
            <div style={{ width: "50%", height: 12, borderRadius: 6, background: "var(--nw-line)" }} />
            <div style={{ width: "30%", height: 10, borderRadius: 5, background: "var(--nw-line)" }} />
          </div>
        )}

        {!failed && !loading && (
          <>
            <p data-testid="identity-sharing-state" style={{ margin: 0, fontSize: 14, lineHeight: 1.45, fontWeight: 600, color: "var(--nw-ink)" }}>
              {describeVisibility({ readers, sections })}
            </p>

            <div style={{ display: "flex", flexDirection: "column" }}>
              {SHAREABLE_SECTIONS.map((section) => (
                <div
                  key={section.key}
                  style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, minWidth: 0, borderTop: "1px solid var(--nw-line)", padding: "10px 0" }}
                >
                  <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--nw-ink)" }}>{section.label}</div>
                    <div style={{ fontSize: 12, lineHeight: 1.45, color: "var(--nw-ink-muted)" }}>{section.hint}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    {saving === section.key && <Loader2 size={14} className="animate-spin" color="var(--nw-ink-muted)" aria-hidden="true" />}
                    <ToggleSwitch
                      tapBox
                      checked={sections[section.key]}
                      disabled={saving !== null}
                      onChange={(next) => toggle(section.key, next === true)}
                      aria-label={`Share ${section.label} with my linked coaches`}
                      data-testid={`identity-share-${section.key}`}
                    />
                  </div>
                </div>
              ))}
            </div>

            {refused && (
              <FailureCard
                data-testid="identity-share-failed"
                title="Couldn't change what you're sharing — nothing has changed."
              />
            )}

            <p data-testid="identity-sharing-read-only" style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-lede)" }}>
              {READ_ONLY_NOTICE}
            </p>
            <p data-testid="identity-sharing-limit" style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-lede)" }}>
              {SHARING_LIMIT}
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <Card className="border-slate-200" data-testid="identity-sharing-card">
      <CardContent className="p-4 sm:p-6 space-y-4 min-w-0">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "#0B2E5915" }}
          >
            <Eye size={18} color="#0B2E59" />
          </div>
          <div className="min-w-0">
            <h3 className="font-display font-bold text-lg text-slate-900 break-words">{SHARING_HEADING}</h3>
            <p className="text-sm text-slate-500 break-words">
              Identity &amp; Vision is private by default. Nothing below is shared until you switch it on.
            </p>
          </div>
        </div>

        {failed && (
          <LoadFailureNote
            what="who can see your Identity &amp; Vision"
            onRetry={load}
            testId="identity-sharing-load-failed"
          />
        )}

        {!failed && loading && (
          <p className="text-sm text-slate-500" data-testid="identity-sharing-loading">
            Loading…
          </p>
        )}

        {!failed && !loading && (
          <>
            {/* THE STATE, as a sentence, before any control. See the header. */}
            <p
              className="text-sm text-slate-900 font-medium break-words"
              data-testid="identity-sharing-state"
            >
              {describeVisibility({ readers, sections })}
            </p>

            <div className="space-y-3">
              {SHAREABLE_SECTIONS.map((section) => (
                <div
                  key={section.key}
                  className="flex items-start justify-between gap-3 min-w-0 border-t border-slate-100 pt-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 break-words">{section.label}</p>
                    <p className="text-xs text-slate-500 break-words">{section.hint}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {saving === section.key && <Loader2 size={13} className="animate-spin text-slate-400" />}
                    <Switch
                      checked={sections[section.key]}
                      disabled={saving !== null}
                      onCheckedChange={(next) => toggle(section.key, next === true)}
                      aria-label={`Share ${section.label} with my linked coaches`}
                      data-testid={`identity-share-${section.key}`}
                    />
                  </div>
                </div>
              ))}
            </div>

            <p className="text-xs text-slate-600 break-words" data-testid="identity-sharing-read-only">
              {READ_ONLY_NOTICE}
            </p>
            <p className="text-xs text-slate-500 break-words" data-testid="identity-sharing-limit">
              {SHARING_LIMIT}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
